import React from "react";
import ReactMarkdown from "react-markdown";
import { ActivityBlock } from "../../types/post";

// Read-only renderer for coach-authored Session Note sections (markdown).
export const SessionNotesMarkdown: React.FC<{ content: string }> = ({
  content,
}) => (
  <div className="prose prose-sm max-w-none text-base-content">
    <ReactMarkdown
      components={{
        p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
        strong: ({ children }) => (
          <strong className="font-semibold text-base-content">
            {children}
          </strong>
        ),
        em: ({ children }) => <em className="italic">{children}</em>,
        ul: ({ children }) => (
          <ul className="list-disc ml-5 mb-3 space-y-1">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal ml-5 mb-3 space-y-1">{children}</ol>
        ),
        li: ({ children }) => <li className="pl-1">{children}</li>,
        h1: ({ children }) => (
          <h1 className="text-xl font-bold mb-3">{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-lg font-bold mb-2">{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-base font-bold mb-2">{children}</h3>
        ),
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            {children}
          </a>
        ),
        code: ({ children }) => (
          <code className="bg-base-300 px-1 py-0.5 rounded text-sm font-mono">
            {children}
          </code>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
);

// Extract the non-empty coach-authored note sections from a session's
// activityBlocks (blocks with blockType === "notes" and content).
export const getSessionNoteSections = (
  blocks?: ActivityBlock[] | null,
): { id: string; name: string; content: string }[] => {
  if (!blocks?.length) return [];
  return blocks
    .filter(
      (block) =>
        block.blockType === "notes" && (block.notesContent || "").trim(),
    )
    .map((block) => ({
      id: block.id,
      name: block.name || "Session Note",
      content: (block.notesContent || "").trim(),
    }));
};

// Renders every coach-authored note section for a session (a heading plus the
// markdown body for each). Returns null when the session has no notes so callers
// can drop it into a layout unconditionally.
export const SessionNotes: React.FC<{
  blocks?: ActivityBlock[] | null;
  className?: string;
}> = ({ blocks, className }) => {
  const sections = getSessionNoteSections(blocks);
  if (sections.length === 0) return null;
  return (
    <div className={className ?? "space-y-3"}>
      {sections.map((section) => (
        <div key={section.id} className="bg-base-200 rounded-sm p-4">
          <h4 className="text-xs font-medium text-base-content/70 font-geist mb-2">
            Session
          </h4>
          <SessionNotesMarkdown content={section.content} />
        </div>
      ))}
    </div>
  );
};

export default SessionNotes;
